import { AuditTask, FIELD_TYPE, FieldDecision, FormConfigField, TASK_STATUS } from "./types";

export function byPriorityThenAge(a: AuditTask, b: AuditTask): number {
  if ((b.priority ?? 0) !== (a.priority ?? 0)) return (b.priority ?? 0) - (a.priority ?? 0);
  return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
}

// ----------------------------  Queue tabs  ----------------------------
// Keys are the queue URL paths (/vlmAudit/my-tasks ...). The workspace gets the tab
// it was opened from as ?tab=, so Prev / Next stay inside that tab.

export const QUEUE_TAB_KEYS = ["my-tasks", "completed", "expired"] as const;
export type QueueTabKey = (typeof QUEUE_TAB_KEYS)[number];
export const DEFAULT_QUEUE_TAB: QueueTabKey = "my-tasks";

export const toQueueTab = (value?: string | null): QueueTabKey =>
  (QUEUE_TAB_KEYS as readonly string[]).includes(value ?? "")
    ? (value as QueueTabKey)
    : DEFAULT_QUEUE_TAB;

/** Whether a task with this status is listed under the tab. */
export const isInQueueTab = (status: string, tab: QueueTabKey): boolean => {
  switch (tab) {
    case "completed":
      return status === TASK_STATUS.COMPLETED;
    case "expired":
      return status === TASK_STATUS.EXPIRED;
    default:
      return status !== TASK_STATUS.COMPLETED && status !== TASK_STATUS.EXPIRED;
  }
};

// ----------------------------  "Other" correction values  ----------------------------
// A dropdown correction can be "Other" plus free text, stored in issue_category as
// "Other: <text>" (or just "Other" while the text is empty). A field offers it only
// when its use case config sets allow_other, and the text is then always required.
// Multi-select values are joined with MULTI_VALUE_SEPARATOR.

export const OTHER_LABEL = "Other";
export const OTHER_PREFIX = "Other: ";
export const MULTI_VALUE_SEPARATOR = "||";

export const isOtherOption = (option: string): boolean =>
  option.trim().toLowerCase() === OTHER_LABEL.toLowerCase();

export const isOtherValue = (value: string): boolean =>
  value === OTHER_LABEL || value.startsWith(OTHER_PREFIX);

export const otherText = (value: string): string =>
  value.startsWith(OTHER_PREFIX) ? value.slice(OTHER_PREFIX.length) : "";

export const toOtherValue = (text: string): string => (text ? OTHER_PREFIX + text : OTHER_LABEL);

/** Whether the field offers the built-in "Other" choice with its free-text box. */
export const allowsOther = (field: FormConfigField): boolean => !!field.allow_other;

/**
 * Configured options to render. With allow_other a configured "Other" option is
 * replaced by the built-in one; without it, "Other" is a plain option.
 */
export const correctionOptions = (field: FormConfigField): string[] =>
  field.options.filter(Boolean).filter((o) => !allowsOther(field) || !isOtherOption(o));

/** A correction is complete when set and any picked "Other" has text. */
export const hasCorrectionValue = (field: FormConfigField, value?: string): boolean => {
  if (!value) return false;
  if (!allowsOther(field)) return true;
  const parts =
    field.type === FIELD_TYPE.DROPDOWN_MULTI ? value.split(MULTI_VALUE_SEPARATOR) : [value];
  return parts.every((p) => !isOtherValue(p) || otherText(p).trim() !== "");
};

// ----------------------------  Correction label / details  ----------------------------

export const DEFAULT_CORRECTION_LABEL = "Issue Category";

/** Heading of the correction an Incorrect field asks for (configurable per field). */
export const correctionLabel = (field: FormConfigField): string =>
  field.correction_label?.trim() || DEFAULT_CORRECTION_LABEL;

/** Hover details configured for one dropdown option ("" when none). */
export const optionDetail = (field: FormConfigField, option: string): string =>
  field.option_details?.[option]?.trim() ?? "";

// ----------------------------  Incorrect decisions  ----------------------------

/** Decisions that ask for an issue category. */
export const isIncorrectDecision = (decision?: string): boolean =>
  decision === "incorrect" || decision === "both_incorrect";

export const hasIncorrectDecision = (decisions: Record<string, FieldDecision>): boolean =>
  Object.keys(decisions).some((k) => isIncorrectDecision(decisions[k]?.decision));
